import { useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Close';

// Cream/sage themed date picker. Drop-in replacement for
// `<TextField type="date">` — same prop names where they overlap, no
// external dep on @mui/x-date-pickers / dayjs / date-fns. The value
// is an ISO `YYYY-MM-DD` string in both directions; `onChange(iso)`
// receives the new value (or '' when cleared).
//
// Use cases that motivated it: DOB on Signup (needs ~100 year range
// with fast year-jump), report date filters, package validity, etc.
//
// Props:
//   value       — 'YYYY-MM-DD' (or '' for empty)
//   onChange    — (iso: string) => void
//   label       — TextField label
//   placeholder — shown when value is empty
//   disabled    — boolean
//   minDate     — 'YYYY-MM-DD' (optional clamp)
//   maxDate     — 'YYYY-MM-DD' (optional clamp)
//   fullWidth   — TextField width
//   helperText  — below-field hint
//   error       — boolean (red border + helperText)
//   clearable   — show ✕ in the input when value is set (default true)
//   sx          — passthrough to the TextField wrapper

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// 'YYYY-MM-DD' → '12 May 1990'. Empty string passes through.
const formatDisplay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// JS Date → 'YYYY-MM-DD' using local-time fields (NOT UTC) so the
// picked day matches what the user sees on the calendar regardless
// of timezone. toISOString() would shift across midnight in some
// regions and surface the previous day on the field.
const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const todayISO = () => toISO(new Date());

const DatePicker = ({
  value = '',
  onChange,
  label,
  placeholder = 'DD MMM YYYY',
  disabled = false,
  minDate,
  maxDate,
  fullWidth = true,
  helperText,
  error,
  clearable = true,
  sx,
}) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [yearPickerOpen, setYearPickerOpen] = useState(false);

  // The "viewing" month — separate from the selected value so the
  // user can browse months without committing to a date.
  const initial = value ? new Date(value) : new Date();
  const [viewYear,  setViewYear]  = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());

  // Memo'd so they don't become a fresh Date object on every render
  // — without this, `yearRange` below sees changed deps every cycle
  // and the year-picker grid recomputes for nothing.
  const min = useMemo(() => (minDate ? new Date(minDate) : null), [minDate]);
  const max = useMemo(() => (maxDate ? new Date(maxDate) : null), [maxDate]);

  // 6×7 calendar grid for the current view month. Filler cells (null)
  // pad the leading and trailing slots so weekday columns line up.
  const days = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const startDay = first.getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(viewYear, viewMonth, d));
    while (cells.length < 42) cells.push(null);
    return cells;
  }, [viewYear, viewMonth]);

  // Year picker range — derived from min/maxDate when supplied,
  // otherwise a 121-year window around today (-100 .. +20). Generous
  // by default so DOB use-cases work without specifying minDate.
  const yearRange = useMemo(() => {
    const fromY = min ? min.getFullYear() : new Date().getFullYear() - 100;
    const toY   = max ? max.getFullYear() : new Date().getFullYear() + 20;
    const list = [];
    for (let y = toY; y >= fromY; y -= 1) list.push(y);
    return list;
  }, [min, max]);

  const isDayDisabled = (d) => {
    if (!d) return true;
    if (min && d < min) return true;
    if (max && d > max) return true;
    return false;
  };

  const openPicker = () => {
    if (disabled) return;
    // Sync the view month back to the selected value (if any) every
    // time the picker opens — feels less surprising than the popover
    // remembering wherever the user last browsed.
    if (value) {
      const v = new Date(value);
      if (!Number.isNaN(v.getTime())) {
        setViewYear(v.getFullYear());
        setViewMonth(v.getMonth());
      }
    }
    setYearPickerOpen(false);
    setOpen(true);
  };

  const handleSelect = (d) => {
    if (isDayDisabled(d)) return;
    onChange?.(toISO(d));
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.('');
  };

  const goPrevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else                 { setViewMonth(viewMonth - 1); }
  };
  const goNextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else                  { setViewMonth(viewMonth + 1); }
  };

  // Disable next/prev when stepping would leave the min/max window.
  const prevDisabled = !!(min && new Date(viewYear, viewMonth, 0) < min);
  const nextDisabled = !!(max && new Date(viewYear, viewMonth + 1, 1) > max);

  const TODAY_ISO = todayISO();

  return (
    <Box ref={anchorRef} sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <TextField
        label={label}
        placeholder={placeholder}
        value={formatDisplay(value)}
        // No-op handler so React stays in controlled mode; the field
        // is set readOnly via InputProps so the user can't type into
        // it but the click-to-open still fires.
        onChange={() => {}}
        onClick={openPicker}
        disabled={disabled}
        fullWidth={fullWidth}
        helperText={helperText}
        error={error}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              {clearable && value && !disabled && (
                <IconButton
                  size="small"
                  onClick={handleClear}
                  edge="end"
                  aria-label="Clear date"
                  sx={{ mr: 0.25 }}
                >
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={openPicker}
                disabled={disabled}
                edge="end"
                aria-label="Open calendar"
              >
                <CalendarTodayOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiInputBase-input': { cursor: disabled ? 'default' : 'pointer' },
          ...sx,
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: '14px',
              boxShadow: '0 12px 40px rgba(21,42,30,0.14)',
              border: '1px solid var(--border-l)',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box sx={{ width: 296, bgcolor: '#fff' }}>
          {/* ── Header: ← Month Year → ── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1, py: 1,
              borderBottom: '1px solid var(--border-l)',
              bgcolor: 'var(--cream)',
            }}
          >
            <IconButton
              size="small"
              onClick={goPrevMonth}
              disabled={prevDisabled || yearPickerOpen}
              aria-label="Previous month"
            >
              <ChevronLeftIcon />
            </IconButton>
            <Box
              onClick={() => setYearPickerOpen((v) => !v)}
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: 'var(--ink)',
                cursor: 'pointer',
                px: 1.5, py: 0.625,
                borderRadius: '8px',
                userSelect: 'none',
                transition: 'background 0.14s',
                '&:hover': { bgcolor: '#fff' },
              }}
            >
              {yearPickerOpen ? `Choose year (${viewYear})` : `${MONTHS[viewMonth]} ${viewYear}`}
            </Box>
            <IconButton
              size="small"
              onClick={goNextMonth}
              disabled={nextDisabled || yearPickerOpen}
              aria-label="Next month"
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* ── Year picker (full-width grid) ── */}
          {yearPickerOpen ? (
            <Box
              sx={{
                p: 1.25,
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 0.5,
                height: 268,
                overflowY: 'auto',
              }}
            >
              {yearRange.map((y) => {
                const isSelected = y === viewYear;
                return (
                  <Box
                    key={y}
                    onClick={() => { setViewYear(y); setYearPickerOpen(false); }}
                    sx={{
                      py: 1.25,
                      textAlign: 'center',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      bgcolor: isSelected ? 'var(--sage)' : 'transparent',
                      color: isSelected ? '#fff' : 'var(--ink)',
                      fontWeight: isSelected ? 700 : 500,
                      fontSize: 13,
                      transition: 'all 0.14s',
                      '&:hover': {
                        bgcolor: isSelected ? 'var(--sage)' : 'var(--cream)',
                      },
                    }}
                  >
                    {y}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <>
              {/* ── Weekday header ── */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  px: 1.25, pt: 1.25, pb: 0.5,
                }}
              >
                {WEEKDAYS.map((w, i) => (
                  <Box
                    key={i}
                    sx={{
                      textAlign: 'center',
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: 'var(--stone)',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      py: 0.5,
                    }}
                  >
                    {w}
                  </Box>
                ))}
              </Box>

              {/* ── Day grid ── */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  px: 1.25, pb: 1.25,
                  gap: 0.25,
                }}
              >
                {days.map((d, i) => {
                  const iso       = d ? toISO(d) : '';
                  const isSelected = !!(d && value && iso === value);
                  const isToday    = !!(d && iso === TODAY_ISO);
                  const dis        = isDayDisabled(d);
                  return (
                    <Box
                      key={i}
                      onClick={() => d && !dis && handleSelect(d)}
                      sx={{
                        height: 36,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        fontWeight: isToday || isSelected ? 700 : 500,
                        borderRadius: '8px',
                        cursor: !d || dis ? 'default' : 'pointer',
                        color: !d
                          ? 'transparent'
                          : dis
                            ? 'var(--border-l)'
                            : isSelected
                              ? '#fff'
                              : 'var(--ink)',
                        bgcolor: isSelected ? 'var(--sage)' : 'transparent',
                        border: isToday && !isSelected
                          ? '1px solid var(--sage)'
                          : '1px solid transparent',
                        transition: 'all 0.12s',
                        '&:hover': d && !dis && !isSelected
                          ? { bgcolor: 'var(--cream)' }
                          : undefined,
                      }}
                    >
                      {d ? d.getDate() : ''}
                    </Box>
                  );
                })}
              </Box>

              {/* ── Footer: Today shortcut + Cancel ── */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  px: 1.25, py: 1,
                  borderTop: '1px solid var(--border-l)',
                  bgcolor: 'var(--cream)',
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => {
                    const today = new Date();
                    if (max && today > max) return;
                    if (min && today < min) return;
                    handleSelect(today);
                  }}
                  sx={{
                    background: 'none',
                    border: 'none',
                    fontFamily: 'inherit',
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'var(--sage)',
                    cursor: 'pointer',
                    px: 1, py: 0.5,
                    borderRadius: '6px',
                    '&:hover': { bgcolor: '#fff' },
                  }}
                >
                  Today
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setOpen(false)}
                  sx={{
                    background: 'none',
                    border: 'none',
                    fontFamily: 'inherit',
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--stone)',
                    cursor: 'pointer',
                    px: 1, py: 0.5,
                    borderRadius: '6px',
                    '&:hover': { bgcolor: '#fff', color: 'var(--ink)' },
                  }}
                >
                  Cancel
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default DatePicker;
